import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
  CircularProgress,
  Link,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Gavel as GavelIcon,
  Shield as ShieldIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  useAcceptTermsMutation,
  useGetTosAcceptanceQuery,
} from "components/Slices/usersApiSlice";

interface TermsAgreementStepProps {
  onNext: () => void;
  onBack: () => void;
  isReadOnly?: boolean;
}

export const TermsAgreementStep: React.FC<TermsAgreementStepProps> = ({
  onNext,
  onBack,
  isReadOnly = false,
}) => {
  const theme = useTheme();
  const [hasClickedTerms, setHasClickedTerms] = useState(false);
  const [hasClickedPrivacy, setHasClickedPrivacy] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [acceptTerms, { isLoading }] = useAcceptTermsMutation();
  const { data: tosData, isLoading: isTosLoading } = useGetTosAcceptanceQuery();
  const [error, setError] = useState<string | null>(null);

  const handleLinkClick = (type: "terms" | "privacy") => {
    if (type === "terms") {
      setHasClickedTerms(true);
    } else {
      setHasClickedPrivacy(true);
    }
  };

  const handleSubmit = async () => {
    if (!isAgreed) return;

    setError(null);
    try {
      await acceptTerms({
        tosAcceptance: { date: new Date().toISOString() },
      }).unwrap();
      onNext();
    } catch (err: any) {
      setError(
        err?.data?.message || "Failed to record agreement. Please try again."
      );
    }
  };

  // Show loading state while checking ToS acceptance
  if (isTosLoading || isLoading) {
    return (
      <Stack spacing={3} alignItems="center" justifyContent="center" py={4}>
        <CircularProgress aria-label="Checking agreement status" />
        <Typography variant="body2" color="text.secondary">
          Checking agreement status...
        </Typography>
      </Stack>
    );
  }

  const tosAcceptance = tosData?.tosAcceptance;
  const allLinksClicked = hasClickedTerms && hasClickedPrivacy;

  // If already accepted (or in read-only mode), show success and continue
  if (isReadOnly || tosAcceptance) {
    return (
      <Stack spacing={3}>
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.success.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <CheckCircleIcon
              sx={{ fontSize: 40, color: theme.palette.success.main }}
            />
          </Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Terms Accepted
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have already accepted the Terms of Service.
          </Typography>
          {tosAcceptance?.date && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Accepted on{" "}
              {new Date(tosAcceptance.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ py: 1.5, flex: 1 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={onNext}
            endIcon={<ArrowForwardIcon />}
            sx={{ py: 1.5, flex: 2 }}
          >
            Continue
          </Button>
        </Box>
      </Stack>
    );
  }

  const links = [
    {
      label: "Terms of Service",
      href: "/terms",
      icon: <GavelIcon />,
      clicked: hasClickedTerms,
      type: "terms" as const,
      description: "Your obligations and rights when using Sanduuq",
    },
    {
      label: "Privacy Policy",
      href: "/privacy",
      icon: <SecurityIcon />,
      clicked: hasClickedPrivacy,
      type: "privacy" as const,
      description: "How we collect, use, and protect your data",
    },
  ];

  return (
    <Stack spacing={3}>
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <GavelIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
        </Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Review & Accept Terms
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please read our Terms of Service and Privacy Policy before continuing
        </Typography>
      </Box>

      {/* Links to Terms */}
      <Stack spacing={2}>
        {links.map((link) => (
          <Paper
            key={link.type}
            elevation={0}
            sx={{
              p: 2,
              border: `2px solid ${
                link.clicked
                  ? theme.palette.success.main
                  : theme.palette.divider
              }`,
              borderRadius: 2,
              transition: "all 0.2s",
              bgcolor: link.clicked
                ? alpha(theme.palette.success.main, 0.03)
                : "transparent",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: link.clicked
                      ? alpha(theme.palette.success.main, 0.1)
                      : alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: link.clicked
                      ? theme.palette.success.main
                      : theme.palette.primary.main,
                  }}
                >
                  {link.clicked ? <CheckCircleIcon /> : link.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {link.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {link.description}
                  </Typography>
                </Box>
              </Box>
              <Link
                href={link.href}
                target="_blank"
                onClick={() => handleLinkClick(link.type)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  textDecoration: "none",
                  color: link.clicked ? "success.main" : "primary.main",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {link.clicked ? "Reviewed" : "Read"}
                <OpenInNewIcon fontSize="small" />
              </Link>
            </Box>
          </Paper>
        ))}
      </Stack>

      {/* Instruction */}
      {!allLinksClicked && (
        <Alert
          severity="info"
          icon={<DescriptionIcon />}
          sx={{ borderRadius: 2 }}
        >
          <Typography variant="body2">
            Click on each document above to review it before accepting
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Agreement Checkbox */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `2px solid ${
            isAgreed ? theme.palette.primary.main : theme.palette.divider
          }`,
          borderRadius: 2,
          transition: "border-color 0.2s",
          opacity: allLinksClicked ? 1 : 0.5,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
              disabled={!allLinksClicked}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I have read and agree to the <strong>Terms of Service</strong> and{" "}
              <strong>Privacy Policy</strong>. I understand that my payment
              obligations in lending circles are binding commitments.
            </Typography>
          }
        />
      </Paper>

      {/* Info about what happens */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 2,
          bgcolor: alpha(theme.palette.info.main, 0.05),
          borderRadius: 2,
        }}
      >
        <ShieldIcon color="info" fontSize="small" />
        <Typography variant="caption" color="text.secondary">
          Your agreement timestamp will be securely recorded for legal
          compliance.
        </Typography>
      </Box>

      {/* Buttons */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={onBack}
          disabled={isLoading}
          startIcon={<ArrowBackIcon />}
          sx={{ py: 1.5, flex: 1 }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={!isAgreed || isLoading}
          endIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <ArrowForwardIcon />
            )
          }
          sx={{
            py: 1.5,
            flex: 2,
            background: isAgreed
              ? `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
              : undefined,
          }}
        >
          {isLoading ? "Saving..." : "I Agree & Continue"}
        </Button>
      </Box>
    </Stack>
  );
};
